import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import './LoginPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003';

const loadFontAwesome = () => {
    if (!document.getElementById('font-awesome-css')) {
        const link = document.createElement('link');
        link.id = 'font-awesome-css';
        link.rel = 'stylesheet';
        link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
        document.head.appendChild(link);
    }
};

function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showBiometricSetup, setShowBiometricSetup] = useState(false);
    const [biometricAvailable, setBiometricAvailable] = useState(false);
    const [biometricEmail, setBiometricEmail] = useState('');
    const [tempToken, setTempToken] = useState(null);
    const [tempUser, setTempUser] = useState(null);

    useEffect(() => {
        loadFontAwesome();
        // Verificar si WebAuthn esta disponible y si hay credenciales registradas
        const savedEmail = localStorage.getItem('biometric_email');
        if (savedEmail && window.PublicKeyCredential) {
            setBiometricAvailable(true);
            setBiometricEmail(savedEmail);
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await axios.post(`${API_BASE_URL}/api/login`, { email, password });
            if (response.data.success) {
                // Verificar si puede registrar biometria
                const canBiometric = window.PublicKeyCredential &&
                    !localStorage.getItem('biometric_email');

                if (canBiometric) {
                    // Guardar token temporalmente y ofrecer biometria
                    setTempToken(response.data.token);
                    setTempUser(response.data.user);
                    setShowBiometricSetup(true);
                } else {
                    // Login directo
                    completeLogin(response.data.token, response.data.user);
                }
            }
        } catch (err) {
            console.error('Error en login:', err);
            setError(err.response?.data?.error || 'Error al iniciar sesion. Verifica tus credenciales.');
        } finally {
            setLoading(false);
        }
    };

    const completeLogin = (token, user) => {
        localStorage.setItem('token', token);
        localStorage.setItem('isAuthenticated', 'true');
        if (user) {
            localStorage.setItem('user', JSON.stringify(user));
        }
        window.location.href = '/conversations';
    };

    const handleBiometricRegister = async () => {
        setError('');
        setLoading(true);
        try {
            // 1. Obtener opciones de registro del servidor
            const optionsRes = await axios.post(
                `${API_BASE_URL}/api/webauthn/register-options`,
                {},
                { headers: { 'Authorization': `Bearer ${tempToken}` } }
            );

            // 2. Crear credencial con el navegador (trigger huella/face)
            const attResp = await startRegistration({ optionsJSON: optionsRes.data });

            // 3. Verificar con el servidor
            const verifyRes = await axios.post(
                `${API_BASE_URL}/api/webauthn/register-verify`,
                attResp,
                { headers: { 'Authorization': `Bearer ${tempToken}` } }
            );

            if (verifyRes.data.verified) {
                // Guardar el email para futuros logins biometricos
                localStorage.setItem('biometric_email', email);
                completeLogin(tempToken, tempUser);
            }
        } catch (err) {
            console.error('Error registrando biometria:', err);
            // Si falla, hacer login normal
            completeLogin(tempToken, tempUser);
        } finally {
            setLoading(false);
        }
    };

    const handleBiometricLogin = async () => {
        setError('');
        setLoading(true);
        try {
            // 1. Obtener opciones de autenticacion
            const optionsRes = await axios.post(
                `${API_BASE_URL}/api/webauthn/login-options`,
                { email: biometricEmail }
            );

            const { userId, ...authOptions } = optionsRes.data;

            // 2. Autenticar con biometria (trigger huella/face)
            const authResp = await startAuthentication({ optionsJSON: authOptions });

            // 3. Verificar con el servidor
            const verifyRes = await axios.post(
                `${API_BASE_URL}/api/webauthn/login-verify`,
                { ...authResp, userId }
            );

            if (verifyRes.data.success) {
                completeLogin(verifyRes.data.token, verifyRes.data.user);
            }
        } catch (err) {
            console.error('Error en login biometrico:', err);
            if (err.response?.status === 404) {
                // Credenciales no encontradas, limpiar
                localStorage.removeItem('biometric_email');
                setBiometricAvailable(false);
                setError('Credencial biometrica no encontrada. Inicia sesion con tu contrasena.');
            } else {
                setError('Error con la autenticacion biometrica. Usa tu contrasena.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSkipBiometric = () => {
        completeLogin(tempToken, tempUser);
    };

    // Pantalla de configuracion de biometria (despues del login exitoso)
    if (showBiometricSetup) {
        return (
            <div className="login-container">
                <div className="login-card">
                    <div className="login-icon">
                        <i className="fas fa-fingerprint"></i>
                    </div>
                    <h2 className="login-title">Acceso rapido</h2>
                    <p className="login-subtitle">
                        Activa el acceso con huella digital o Face ID para entrar mas rapido la proxima vez.
                    </p>
                    <button
                        className="login-btn-primary"
                        onClick={handleBiometricRegister}
                        disabled={loading}
                    >
                        {loading ? (
                            <><i className="fas fa-spinner fa-spin"></i> Configurando...</>
                        ) : (
                            <><i className="fas fa-fingerprint"></i> Activar biometria</>
                        )}
                    </button>
                    <button
                        className="login-btn-secondary"
                        onClick={handleSkipBiometric}
                        disabled={loading}
                    >
                        Ahora no
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-icon">
                    <i className="fas fa-comments" style={{ color: 'var(--accent-primary)' }}></i>
                </div>
                <h2 className="login-title">Iniciar Sesion</h2>

                {/* Boton de login biometrico */}
                {biometricAvailable && (
                    <div className="biometric-section">
                        <button
                            className="login-btn-biometric"
                            onClick={handleBiometricLogin}
                            disabled={loading}
                        >
                            {loading ? (
                                <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                                <i className="fas fa-fingerprint"></i>
                            )}
                        </button>
                        <p className="biometric-label">Acceder con huella</p>
                        <div className="login-divider">
                            <span>o inicia con contrasena</span>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} autoComplete="off">
                    <div className="login-field">
                        <label className="login-label">Email</label>
                        <input
                            type="email"
                            className="login-input"
                            placeholder="tu@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="off"
                        />
                    </div>
                    <div className="login-field">
                        <label className="login-label">Contrasena</label>
                        <input
                            type="password"
                            className="login-input"
                            placeholder="Tu contrasena"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                        />
                    </div>
                    {error && <div className="login-error">{error}</div>}
                    <button
                        type="submit"
                        className="login-btn-primary"
                        disabled={loading}
                    >
                        {loading ? 'Iniciando sesion...' : 'Entrar'}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default LoginPage;
